import { Base } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import Cookies from 'js-cookie';

/**
 * Homepage class.
 */
export default class Projects extends Base {
  /**
   * Class config
   * @type {Object}
   */
  static config = {
    name: 'Projects',
    emits: ['update-app'],
    refs: [
      'container',
      'card[]',
      'cardMask[]',
      'stickyYear',
      'years[]',
      'yearsNav',
      'leftCol',
      'rightCol',
      'yearsLink[]',
    ],
  };

  /**
   * Mount component
   * @returns {void}
   */
  async mounted() {
    if (sessionStorage.getItem('scroll_position')) {
      window.scrollTo({
        top: sessionStorage.getItem('scroll_position'),
      });
    }

    if (sessionStorage.getItem('current_year') && this.$refs.stickyYear) {
      this.$refs.stickyYear.textContent = sessionStorage.getItem('current_year');
    }

    this.data = '';
    this.year = this.$refs.stickyYear ? this.$refs.stickyYear : '';
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(ScrollToPlugin);

    this.template = Cookies.get('layout') ? Cookies.get('layout') : 'grid';
    if (this.template === 'grid') {
      this.initGrid();
    } else {
      this.showList();
    }
  }

  /**
   * Set Scroll Position in session storage
   * @returns {void}
   */
  onCardClick() {
    sessionStorage.setItem('scroll_position', window.scrollY);
    if (this.$refs.stickyYear) {
      sessionStorage.setItem('current_year', this.$refs.stickyYear.textContent);
    }
  }

  /**
   * Scroll to year Collection when click on year link
   * @returns {void}
   * @param  {Event} e
   */
  onYearsLinkClick(e) {
    const { year } = e.currentTarget.dataset;
    const section = document.querySelector(`[data-year-anchor="${year}"]`);

    gsap.to(window, { duration: 1, scrollTo: section, ease: 'Expo.easeOut' });
  }

  /**
   * Init parallax on grid display
   * @returns {void}
   */
  initGrid() {
    const observer = new IntersectionObserver(this.checkIntersection.bind(this), {
      threshold: 0.5,
    });

    this.$refs.card.forEach((target) => {
      observer.observe(target);
    });

    this.showGrid();
  }

  /**
   * Check years of project base on scroll
   * @param  {Array} entries
   * @returns {void}
   */
  checkIntersection(entries) {
    if (entries[0].isIntersecting) {
      const { year } = entries[0].target.dataset;
      if (year !== this.year) {
        this.$refs.stickyYear.innerHTML = year;
        this.year = year;
      }
    }
  }

  /**
   * Change Layout
   * @param  {string} template
   * @param  {string} data
   * @returns {void}
   */
  changeLayout(template, data) {
    this.data = data;
    if (this.template === template) return;

    if (template === 'list') this.changeToList();
    else this.changeToGrid();

    this.template = template;
  }

  /**
   * Thumbs list display
   * @returns {void}
   */
  changeToList() {
    gsap.to(this.$refs.stickyYear, { opacity: 0, y: '-20px', ease: 'power3.out' });
    gsap
      .to(this.$refs.cardMask, { scaleX: 1, stagger: { each: 0.005 }, ease: 'power3.out' })
      .then(() => {
        window.scroll(0, 0);
        if (this.data) {
          this.$refs.container.innerHTML = this.data;
          this.$update();
          this.$emit('update-app', 'list');
        }
        this.showList();
      });
  }

  /**
   * Grid display
   * @returns {void}
   */
  changeToGrid() {
    gsap.to(this.$refs.yearsNav, { opacity: 0, ease: 'power3.out' });
    gsap.to(this.$refs.card, { x: '-100%', stagger: { each: 0.005 }, ease: 'power3.out' });
    gsap.to(this.$refs.years, { opacity: 0 }).then(() => {
      setTimeout(() => {
        window.scroll(0, 0);
        if (this.data) {
          this.$refs.container.innerHTML = this.data;
          this.$update();
          this.$emit('update-app', 'grid');
        }
        this.initGrid();
      }, 1000);
    });
  }

  /**
   * Juste show list without fetch data
   * @returns {void}
   */
  showList() {
    gsap.to(this.$refs.yearsNav, { opacity: 1 });
    gsap.to(this.$refs.card, { x: 0, stagger: { each: 0.005, ease: 'power3.in.out' } });
    gsap.fromTo(this.$refs.years, { y: '50px', opacity: 0 }, { y: 0, opacity: 1, delay: 0.25 });
  }

  /**
   * Show grid without fetch data
   * @returns {void}
   */
  showGrid() {
    gsap.to(this.$refs.stickyYear, { opacity: 0, duration: 0 });
    gsap.to(this.$refs.cardMask, { scaleX: 1, duration: 0 });
    gsap.to(this.$refs.stickyYear, { opacity: 1, duration: 1, ease: 'power3.out' }, '+=0.5');
    gsap.to(this.$refs.cardMask, { scaleX: 0, stagger: { each: 0.005 }, ease: 'power3.out' });
  }
}
